/**
 * The Classy Kitchen — Collection Template
 * assets/tan-collection.js
 *
 * Vanilla JS, no dependencies. Progressively enhances a fully server-rendered
 * collection page: works with JS disabled (native links/forms), and upgrades
 * to AJAX filtering/sorting + load-more + quick add when JS is available.
 *
 * FIX: the AJAX facet-filtering code used to be wrapped in its own nested
 * IIFE, which meant applySort()/fetchFacetedResults() etc. were invisible to
 * the mobile Sort Drawer click handler defined earlier in the file. That
 * threw "ReferenceError: applySort is not defined" whenever someone tapped a
 * sort option in the mobile drawer. The nested IIFE has been unwrapped so
 * every helper shares one scope.
 *
 * FIX2: tag filters (color/size/...) live at Shopify's URL PATH level
 * (/collections/handle/color_red), while native filters (price,
 * availability) live in the QUERY STRING (?filter.v.price.gte=...). Every
 * AJAX fetch here used to be built as `collectionUrl + '?' + params`, where
 * collectionUrl is `{{ collection.url }}` — and Shopify's `collection.url`
 * NEVER includes an active tag path segment, no matter what page you're on.
 * So toggling a price checkbox while a color tag was active silently
 * dropped the color tag from the request (and clicking a tag link wasn't
 * even AJAX-intercepted at all, so it always fell back to a hard reload).
 *
 * Fix: `currentCollectionPath` now tracks whichever tag-inclusive path is
 * actually in effect (seeded from window.location.pathname on load, updated
 * after every fetch/tag click/batan-forward navigation), and
 * fetchFacetedResults() uses THAT as the request path by default instead of
 * the fixed base collectionUrl. Tag links now also carry
 * data-ajax-filter-link (see snippets/tan-filter-panel.liquid) so they're
 * routed through this same path-aware fetch instead of a full page reload.
 */
(function () {
  'use strict';

  var root = document.getElementById('MainCollection');
  if (!root) return;

  var collectionUrl = root.dataset.collectionUrl; // base collection path — Shopify never includes tag segments here
  var sectionId = root.dataset.sectionId;
  var currentCollectionPath = window.location.pathname; // FIX2: tag-inclusive path currently in effect
  var debounceTimer = null;

  /* ------------------------------------------------------------------ */
  /* Utilities                                                          */
  /* ------------------------------------------------------------------ */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { fn.apply(context, args); }, wait);
    };
  }

  function trapFocus(container) {
    var focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])', container);
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return function () { container.removeEventListener('keydown', handler); };
  }

  /* ------------------------------------------------------------------ */
  /* Drawers (filter + sort)                                            */
  /* ------------------------------------------------------------------ */
  var releaseFocusTrap = null;
  var lastFocusedEl = null;

  function openDrawer(drawer, triggerBtn) {
    if (!drawer) return;
    lastFocusedEl = triggerBtn || document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    releaseFocusTrap = trapFocus(drawer.querySelector('.tan-drawer__panel') || drawer);
    if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'true');
  }

  function closeDrawer(drawer) {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    if (releaseFocusTrap) { releaseFocusTrap(); releaseFocusTrap = null; }
    setTimeout(function () { drawer.hidden = true; }, 280);
    if (lastFocusedEl) lastFocusedEl.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('.tan-filter-open');
    if (opener) { openDrawer(qs('#FilterDrawer'), opener); return; }

    var sortOpener = e.target.closest('#MobileSortTrigger');
    if (sortOpener) { openDrawer(qs('#SortDrawer'), sortOpener); return; }

    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      var drawer = closer.closest('.tan-drawer');
      closeDrawer(drawer);
      return;
    }

    var sortOption = e.target.closest('.tan-sort-option');
    if (sortOption) {
      applySort(sortOption.dataset.sortValue); // FIX: applySort is now in the same scope
      closeDrawer(qs('#SortDrawer'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    qsa('.tan-drawer.is-open').forEach(closeDrawer);
    closeQuickAddModal();
  });

  /* ------------------------------------------------------------------ */
  /* AJAX facet filtering + sorting                                     */
  /* FIX: this used to be wrapped in its own nested IIFE with duplicate */
  /* qs/qsa/debounce declarations. Unwrapped so applySort(),            */
  /* fetchFacetedResults(), currentParams() etc. are reachable from the */
  /* click/change handlers defined elsewhere in this file (e.g. the     */
  /* mobile Sort Drawer handler above).                                 */
  /* ------------------------------------------------------------------ */

  function getCurrentFacetForm() {
    /*
     * Prefer the visible/current form.
     * This prevents the hidden mobile drawer form from
     * overriding the desktop sidebar form.
     */
    var drawerForm = qs('#FilterDrawer [data-facet-form]');
    var sidebarForm = qs('.tan-sidebar [data-facet-form]');

    if (drawerForm && isVisible(drawerForm)) return drawerForm;
    if (sidebarForm && isVisible(sidebarForm)) return sidebarForm;

    return drawerForm || sidebarForm || qs('[data-facet-form]');
  }

  function isVisible(element) {
    if (!element) return false;
    var style = window.getComputedStyle(element);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.offsetWidth > 0 &&
      element.offsetHeight > 0
    );
  }

  /* ---------------------------------------------------------
     Build filter parameters (query string only — native
     filters + sort. Tag state lives in currentCollectionPath,
     see FIX2 above.)
     --------------------------------------------------------- */
  function currentParams() {
    var params = new URLSearchParams(window.location.search);
    var form = getCurrentFacetForm();

    // Remove existing Shopify facet parameters. Keep sort_by and anything else.
    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') {
        params.delete(key);
      }
    });

    if (form) {
      var formData = new FormData(form);
      formData.forEach(function (value, key) {
        if (value === '') return;
        if (key.indexOf('filter.') === 0) {
          params.append(key, value);
        }
      });
    }

    return params;
  }

  /* ---------------------------------------------------------
     FIX2: how many tag segments are active in a given path,
     relative to the base collection path. Used for the
     "Filter (n)" badge in the toolbar.
     --------------------------------------------------------- */
  function getActiveTagCount(path) {
    if (!collectionUrl) return 0;
    var base = collectionUrl.replace(/\/$/, '');
    var current = (path || currentCollectionPath).replace(/\/$/, '');
    if (current === base) return 0;
    if (current.indexOf(base + '/') !== 0) return 0;
    var remainder = current.slice(base.length + 1);
    if (!remainder) return 0;
    return remainder.split('+').filter(Boolean).length;
  }

  /* ---------------------------------------------------------
     AJAX collection request
     path: the tag-inclusive collection path to request.
     Defaults to whatever is currently active, so a native
     filter/sort change doesn't accidentally drop an active
     tag filter. Pass an explicit path (from a link's href)
     when the tag state itself is changing — see the
     [data-ajax-filter-link] handler below.
     --------------------------------------------------------- */
  function fetchFacetedResults(params, pushHistory, path) {
    if (!params) params = new URLSearchParams(window.location.search);
    if (pushHistory === undefined) pushHistory = true;
    if (!path) path = currentCollectionPath; // FIX2

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    var url = path + '?section_id=' + encodeURIComponent(sectionId);
    var query = params.toString();
    if (query) url += '&' + query;

    fetch(url, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Collection request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        replaceRegion(doc, '#CollectionGrid');
        replaceRegion(doc, '#FilterDrawerContent');
        replaceRegion(doc, '#FilterSidebar');
        replaceRegion(doc, '#ActiveFilters');
        replaceRegion(doc, '#ProductCount');
        replaceRegion(doc, '#CollectionCount');

        currentCollectionPath = path; // FIX2: remember so the NEXT change (tag or native) keeps this context

        syncSortValue(params);
        updateActiveFilterCount(params);

        if (pushHistory) {
          var newUrl = path + (params.toString() ? '?' + params.toString() : '');
          window.history.pushState({ collectionFilter: true }, '', newUrl);
        }

        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');

        var grid = qs('#CollectionGrid');
        if (grid) grid.setAttribute('tabindex', '-1');

        observeCards();

        if (typeof window.initCollection === 'function') {
          window.initCollection();
        }
      })
      .catch(function (error) {
        console.error('Classy Kitchen filter error:', error);
        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');
      });
  }

  function replaceRegion(doc, selector) {
    var incoming = doc.querySelector(selector);
    var current = document.querySelector(selector);
    if (!incoming || !current) return;
    current.replaceWith(incoming.cloneNode(true));
  }

  function updateActiveFilterCount(params) {
    var count = 0;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 && value !== '') count++;
    });
    count += getActiveTagCount(); // FIX2: tags now count toward the "Filter (n)" badge too

    qsa('#ActiveFilterCount').forEach(function (el) {
      if (count > 0) {
        el.hidden = false;
        el.textContent = count;
      } else {
        el.hidden = true;
        el.textContent = '';
      }
    });
  }

  function applySort(value) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page'); // reset pagination when sorting
    if (value) params.set('sort_by', value);
    else params.delete('sort_by');
    fetchFacetedResults(params, true); // path defaults to currentCollectionPath, so active tags survive a sort change
  }

  function syncSortValue(params) {
    var sortValue = params.get('sort_by');
    qsa('#SortBy').forEach(function (select) {
      if (sortValue) select.value = sortValue;
    });
  }

  /* FILTER CHANGE (checkbox / price input / desktop sort <select>) */
  document.addEventListener('change', function (event) {
    var target = event.target;

    if (target && target.matches('[data-facet-input]')) {
      debounce(function () {
        var params = currentParams();
        params.delete('page');
        fetchFacetedResults(params, true); // path defaults to currentCollectionPath, preserving any active tag
      }, 250)();
      return;
    }

    if (target && target.matches('#SortBy')) {
      applySort(target.value);
    }
  });

  /* FILTER FORM SUBMIT (progressive-enhancement fallback) */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form && form.matches('[data-facet-form]'))) return;

    event.preventDefault();

    var params = new URLSearchParams(window.location.search);
    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') params.delete(key);
    });

    var formData = new FormData(form);
    formData.forEach(function (value, key) {
      if (value !== '' && key.indexOf('filter.') === 0) params.append(key, value);
    });

    fetchFacetedResults(params, true);
  });

  /* AJAX FILTER LINKS — tag toggles (color/size swatches), single-filter
     removal pills, and "Clear all filters" links all carry
     data-ajax-filter-link. FIX2: we now also read the link's own pathname
     and pass it through as the request path, instead of discarding it and
     always fetching the base collection URL. This is what makes clicking a
     tag swatch stay in-page (AJAX) instead of a hard reload, and what keeps
     "remove this one filter" pills from wiping out an active tag (or
     vice versa). */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-ajax-filter-link]');
    if (!link) return;

    event.preventDefault();

    var url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      console.error('Invalid filter URL:', error);
      return;
    }

    var params = url.searchParams;
    params.delete('page');
    fetchFacetedResults(params, true, url.pathname); // FIX2: pass the link's path (may include a tag segment, or be the bare base for "clear")
  });

  /* CLEAR FILTERS (explicit data-clear-filters trigger, if used) */
  document.addEventListener('click', function (event) {
    var clearButton = event.target.closest('[data-clear-filters]');
    if (!clearButton) return;

    event.preventDefault();

    var params = new URLSearchParams(window.location.search);
    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') params.delete(key);
    });

    fetchFacetedResults(params, true, collectionUrl); // explicit clear resets to the tag-free base path too
  });

  /* Browser Back / Forward */
  window.addEventListener('popstate', function () {
    var params = new URLSearchParams(window.location.search);
    fetchFacetedResults(params, false, window.location.pathname); // FIX2: restore whatever tag path was active at that history entry
  });

  /* ------------------------------------------------------------------ */
  /* Load more pagination                                               */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.tan-load-more');
    if (!btn) return;
    btn.dataset.loading = 'true';
    btn.textContent = 'Loading…';

    fetch(btn.dataset.nextUrl + (btn.dataset.nextUrl.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId))
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newItems = qsa('.tan-grid__item', doc);
        var grid = qs('.tan-grid');
        newItems.forEach(function (item) { grid.appendChild(item); });

        var newPagination = doc.querySelector('#Pagination');
        var currentPagination = qs('#Pagination');
        if (newPagination && currentPagination) currentPagination.replaceWith(newPagination);
        else if (currentPagination) currentPagination.remove();

        observeCards();
      })
      .catch(function () {
        btn.dataset.loading = 'false';
        btn.textContent = 'Load more';
      });
  });

  /* ------------------------------------------------------------------ */
  /* Entrance animation on scroll (respects reduced motion via CSS)     */
  /* ------------------------------------------------------------------ */
  var io;
  var animationsEnabled = root.dataset.animations === 'true';
  function observeCards() {
    var items = qsa('.tan-grid__item');

    if (!animationsEnabled || !('IntersectionObserver' in window)) {
      items.forEach(function (item) { item.classList.remove('tan-anim-init'); });
      return;
    }

    if (io) io.disconnect();
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '80px' });

    items.forEach(function (item, i) {
      item.classList.add('tan-anim-init');
      item.classList.remove('is-visible');
      item.style.transitionDelay = Math.min(i * 30, 240) + 'ms';
      io.observe(item);
    });

    setTimeout(function () {
      qsa('.tan-grid__item.tan-anim-init:not(.is-visible)').forEach(function (item) {
        item.classList.add('is-visible');
      });
    }, 1200);
  }
  observeCards();

  /* ------------------------------------------------------------------ */
  /* Wishlist (local, front-end only — wire to your wishlist app/API)   */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    var pressed = btn.getAttribute('aria-pressed') === 'true';
    btn.setAttribute('aria-pressed', String(!pressed));
    document.dispatchEvent(new CustomEvent('wishlist:toggle', {
      detail: { productId: btn.dataset.productId, added: !pressed }
    }));
  });

  /* ------------------------------------------------------------------ */
  /* Quick add — single variant: direct add. Multi variant: modal.      */
  /* ------------------------------------------------------------------ */
  var qaModal = null;

  function buildModal() {
    if (qaModal) return qaModal;
    qaModal = document.createElement('div');
    qaModal.className = 'tan-qa-modal';
    qaModal.hidden = true;
    qaModal.setAttribute('role', 'dialog');
    qaModal.setAttribute('aria-modal', 'true');
    qaModal.innerHTML =
      '<div class="tan-qa-modal__backdrop" data-qa-close></div>' +
      '<div class="tan-qa-modal__panel">' +
        '<button type="button" class="tan-qa-modal__close" data-qa-close aria-label="Close">' +
          '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="tan-qa-modal__head">' +
          '<img class="tan-qa-modal__img" alt="">' +
          '<div><h2 class="tan-qa-modal__title"></h2><p class="tan-qa-modal__price"></p></div>' +
        '</div>' +
        '<div class="tan-qa-modal__options"></div>' +
        '<button type="button" class="tan-btn tan-btn--primary tan-qa-modal__add" style="width:100%">Add to cart</button>' +
        '<p class="tan-qa-modal__status">✓ Added to cart</p>' +
        '<div class="tan-qa-modal__post-actions">' +
          '<a class="tan-btn tan-btn--outline" data-qa-close>Continue shopping</a>' +
          '<a class="tan-btn tan-btn--primary" href="' + (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart' : '/cart') + '">View cart</a>' +
        '</div>' +
      '</div>';
    document.body.appendChild(qaModal);
    qaModal.addEventListener('click', function (e) {
      if (e.target.closest('[data-qa-close]')) closeQuickAddModal();
    });
    return qaModal;
  }

  function closeQuickAddModal() {
    if (!qaModal || qaModal.hidden) return;
    qaModal.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { qaModal.hidden = true; }, 200);
  }

  function openQuickAddModal(productData, triggerBtn) {
    var modal = buildModal();
    lastFocusedEl = triggerBtn;
    modal.querySelector('.tan-qa-modal__img').src = productData.image || '';
    modal.querySelector('.tan-qa-modal__title').textContent = productData.title;
    modal.querySelector('.tan-qa-modal__status').classList.remove('is-visible');
    modal.querySelector('.tan-qa-modal__post-actions').classList.remove('is-visible');
    modal.querySelector('.tan-qa-modal__add').style.display = '';

    var selected = {};
    var optionsWrap = modal.querySelector('.tan-qa-modal__options');
    optionsWrap.innerHTML = '';

    productData.options.forEach(function (opt) {
      selected[opt.name] = opt.values[0];
      var group = document.createElement('div');
      group.className = 'tan-qa-option';
      group.innerHTML = '<span class="tan-qa-option__label">' + opt.name + '</span><div class="tan-qa-option__values"></div>';
      var valuesWrap = group.querySelector('.tan-qa-option__values');
      opt.values.forEach(function (val) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'tan-qa-swatch';
        b.textContent = val;
        if (val === selected[opt.name]) b.classList.add('is-selected');
        b.addEventListener('click', function () {
          selected[opt.name] = val;
          qsa('.tan-qa-swatch', valuesWrap).forEach(function (s) { s.classList.remove('is-selected'); });
          b.classList.add('is-selected');
          syncVariant();
        });
        valuesWrap.appendChild(b);
      });
      optionsWrap.appendChild(group);
    });

    function matchVariant() {
      return productData.variants.find(function (v) {
        return productData.options.every(function (opt, i) { return v.options[i] === selected[opt.name]; });
      });
    }

    function syncVariant() {
      var variant = matchVariant();
      var priceEl = modal.querySelector('.tan-qa-modal__price');
      var addBtn = modal.querySelector('.tan-qa-modal__add');
      if (variant) {
        priceEl.textContent = formatMoney(variant.price);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
        addBtn.dataset.variantId = variant.id;
      }
    }
    syncVariant();

    modal.querySelector('.tan-qa-modal__add').onclick = function () {
      var addBtn = modal.querySelector('.tan-qa-modal__add');
      var variantId = addBtn.dataset.variantId;
      if (!variantId) return;
      addToCart(variantId, addBtn).then(function (ok) {
        if (ok) {
          addBtn.style.display = 'none';
          modal.querySelector('.tan-qa-modal__status').classList.add('is-visible');
          modal.querySelector('.tan-qa-modal__post-actions').classList.add('is-visible');
        }
      });
    };

    modal.hidden = false;
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    trapFocus(modal.querySelector('.tan-qa-modal__panel'));
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function addToCart(variantId, btn) {
    if (btn) { btn.disabled = true; btn.dataset.originalText = btn.textContent; btn.textContent = 'Adding…'; }
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_add_url ? window.Shopify.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { variantId: variantId } }));
        var drawer = document.querySelector('cart-drawer, cart-notification');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.originalText || 'Add to cart'; }
        return true;
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.originalText || 'Add to cart'; }
        return false;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quitan-add]');
    if (!btn) return;

    if (btn.dataset.requiresSelection === 'true') {
      var script = document.querySelector('[data-quitan-add-json="' + btn.dataset.productHandle + '"]');
      if (!script) return;
      var data = JSON.parse(script.textContent);
      openQuickAddModal(data, btn);
      return;
    }

    var defaultLabel = btn.dataset.defaultLabel || 'Add to Cart';
    addToCart(btn.dataset.variantId, btn).then(function (ok) {
      if (ok) {
        btn.classList.add('is-added');
        btn.textContent = '✓ Added';
        setTimeout(function () { btn.classList.remove('is-added'); btn.textContent = defaultLabel; }, 1800);
      }
    });
  });
})();
